<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="anna-letters">
    <p class="anna-letters-muted">
      Letters to the candidate: job offers, rejections, welcome letters. Pick
      one, change it right in the letter for this candidate, then send it. A job
      offer makes the candidate Job Offered, a rejection Rejected. The templates
      are on ANNA Settings → Letters. Signing isn't set up yet.
    </p>
    <p v-if="testMode" class="anna-letters-muted">
      Test mode: letters go to {{ to || 'the test address (not set)' }}, not the
      candidate, and the status stays as it is.
    </p>

    <h6 class="anna-letters-title">Sent</h6>
    <p v-if="loading" class="anna-letters-muted">Loading…</p>
    <p v-else-if="!sent.length" class="anna-letters-muted">
      No letters sent yet.
    </p>
    <div v-for="(l, i) in sent" :key="`sent${i}`" class="anna-letters-sent">
      <span class="anna-letters-name">{{ l.title }}</span>
      <span class="anna-letters-muted">
        {{ l.sentAt.slice(0, 16) }} to {{ l.to }}{{ l.test ? ' (test)' : ''
        }}{{ l.by ? ` by ${l.by}` : ''
        }}{{ l.status ? ` · now ${l.status}` : '' }}
      </span>
      <oxd-button
        display-type="ghost"
        size="medium"
        :label="viewing === i ? 'Hide' : 'View'"
        @click="viewing = viewing === i ? null : i"
      />
      <letter-editor
        v-if="viewing === i"
        :html="l.html"
        title="The letter as sent"
        readonly
      />
    </div>

    <template v-if="canAct">
      <h6 class="anna-letters-title">Send a letter</h6>
      <p v-if="!loading && !templates.length" class="anna-letters-muted">
        No letter templates yet (ANNA Settings → Letters).
      </p>
      <div v-else-if="!draft" class="anna-letters-pick">
        <oxd-input-field
          v-model="picked"
          type="select"
          label="Letter"
          :options="templateOptions"
        />
        <oxd-button
          display-type="secondary"
          label="Open the letter"
          :disabled="!picked"
          @click="open"
        />
      </div>
      <template v-else>
        <oxd-input-field v-model="draft.subject" label="Subject" />
        <p class="anna-letters-muted">
          Click into the letter to change it: only this candidate's letter
          changes, not the template.
        </p>
        <letter-editor ref="editor" :key="draft.id" :html="draft.html" />
        <div class="anna-letters-actions">
          <oxd-button
            display-type="ghost"
            label="Discard"
            :disabled="busy"
            @click="draft = null"
          />
          <oxd-button
            display-type="secondary"
            :label="busy ? 'Sending…' : 'Send the letter'"
            :disabled="busy || !draft.subject.trim()"
            @click="send"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import LetterEditor from './LetterEditor.vue';

// A candidate's Documents tab: the letters sent to them, and a letter from a template edited for
// this candidate in place (the template stays as it is), emailed and kept as sent.
export default {
  name: 'CandidateLetters',
  components: {'letter-editor': LetterEditor},
  props: {
    candidateId: {type: Number, required: true},
    canAct: {type: Boolean, default: false},
  },
  emits: ['moved'],
  data() {
    return {
      loading: true,
      templates: [],
      sent: [],
      testMode: null,
      to: null,
      picked: null,
      draft: null,
      viewing: null,
      busy: false,
    };
  },
  computed: {
    templateOptions() {
      return this.templates.map((t) => ({id: t.id, label: t.title}));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    api(path) {
      return new APIService(window.appGlobal.baseUrl, path);
    },
    load() {
      this.loading = true;
      Promise.all([
        this.api(`/api/v2/anna/candidates/${this.candidateId}/letters`)
          .request({method: 'GET'})
          .then(({data: {data}}) => {
            this.sent = data.sent || [];
            this.testMode = data.testMode;
            this.to = data.to;
          }),
        this.canAct
          ? this.api('/api/v2/anna/letter-templates')
              .request({method: 'GET'})
              .then(({data: {data}}) => {
                this.templates = data.templates || [];
              })
          : null,
      ])
        .catch(() => null)
        .finally(() => {
          this.loading = false;
        });
    },
    open() {
      const t = this.templates.find((x) => x.id === this.picked?.id);
      if (t) this.draft = {id: t.id, subject: t.subject, html: t.html};
    },
    send() {
      const html = this.$refs.editor?.getHtml();
      if (!html) return;
      // a real email to the candidate (not test mode): asked first, it can't be taken back
      if (
        this.testMode !== true &&
        !window.confirm(`Email this letter to ${this.to || 'the candidate'}?`)
      )
        return;
      this.busy = true;
      this.api(`/api/v2/anna/candidates/${this.candidateId}/letters`)
        .request({
          method: 'PUT',
          data: {templateId: this.draft.id, subject: this.draft.subject, html},
        })
        .then(({data: {data, meta}}) => {
          const email = meta?.email || {};
          const toast = {
            title: email.sent ? 'Sent' : 'Not sent',
            message:
              [email.sent ? `Emailed to ${email.to}.` : null, email.note]
                .filter(Boolean)
                .join(' ') || 'The letter was not sent.',
          };
          if (!email.sent) return this.$toast.warn(toast);
          this.$toast.success(toast);
          this.sent = data.sent || this.sent;
          this.draft = null;
          // the status and History changed: the page shows them anew
          if (this.sent[this.sent.length - 1]?.status) this.$emit('moved');
        })
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-letters-title {
  margin: 16px 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: #12233f;
}
.anna-letters-muted {
  margin: 0 0 8px;
  font-size: 12px;
  color: #64728c;
}
.anna-letters-sent {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-bottom: 8px;
  .anna-letters-muted {
    margin: 0;
  }
}
.anna-letters-name {
  font-size: 13px;
  font-weight: 700;
}
.anna-letters-pick {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  max-width: 640px;
  > :first-child {
    flex: 1;
  }
}
.anna-letters-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
</style>
